import { useEffect } from "react";
import { Link } from "react-router";
import { GameCard } from "../components/GameCard";
import { GameRow } from "../components/GameRow";
import { Hero } from "../components/Hero";
import { placeholderGames } from "../data/placeholderGames";
import { ARCHETYPE_LABELS, type Archetype } from "../types/Game";

/** Curated slugs for the "Staff Picks" row — hand-picked for variety. */
const STAFF_PICK_SLUGS = [
	"neon-rush",
	"cubefall",
	"battlecards-origins",
	"cozy-harvest",
	"skyhop",
	"vocabular",
];

/**
 * Home page. Hero banner + Staff Picks + Trending + New Releases + Featured +
 * Browse by Genre grid + All Games.
 *
 * Everything comes from the local placeholder catalog for MVP; swap
 * `placeholderGames` for API hooks once the publishing pipeline is live.
 */
export function HomeRoute() {
	useEffect(() => {
		document.title = "GenieWorlds — Games built by creators";
	}, []);

	const staffPicks = placeholderGames.filter((g) =>
		STAFF_PICK_SLUGS.includes(g.slug),
	);
	const trending = [...placeholderGames]
		.filter((g) => g.trending)
		.sort((a, b) => b.plays - a.plays);
	const newReleases = [...placeholderGames]
		.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
		.slice(0, 8);
	const featured = placeholderGames.filter((g) => g.featured);

	// One representative game per archetype for the genre grid
	const byArchetype: Partial<
		Record<Archetype, (typeof placeholderGames)[number]>
	> = {};
	for (const g of placeholderGames) {
		if (!byArchetype[g.archetype]) byArchetype[g.archetype] = g;
	}

	return (
		<div className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-10 space-y-12 md:space-y-16">
			<Hero />

			<GameRow
				title="Staff Picks"
				games={staffPicks}
				seeAllHref="/browse"
			/>

			<GameRow
				title="Trending Now"
				games={trending}
				seeAllHref="/browse"
			/>

			<GameRow
				title="New Releases"
				games={newReleases}
				seeAllHref="/browse"
			/>

			<GameRow
				title="Featured"
				games={featured}
				seeAllHref="/browse"
			/>

			{/* Browse by Genre */}
			<section className="space-y-5">
				<div className="flex items-end justify-between gap-4">
					<div>
						<h2 className="font-display font-bold text-xl md:text-2xl text-ink">
							Browse by genre
						</h2>
						<p className="text-sm text-ink-faint mt-1">
							20 genres, infinite possibilities
						</p>
					</div>
					<Link
						to="/browse"
						className="text-sm text-primary hover:underline shrink-0"
					>
						See all
					</Link>
				</div>
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
					{Object.entries(byArchetype).map(
						([archetype, game]) => {
							if (!game) return null;
							return (
								<Link
									key={archetype}
									to={`/browse?category=${archetype}`}
									className="group relative overflow-hidden rounded-xl border border-border hover:border-primary/40 transition-all duration-300 aspect-[3/2] hover:shadow-[0_0_20px_-6px_var(--color-primary-glow)]"
								>
									<div
										className="absolute inset-0 flex items-center justify-center"
										style={{
											backgroundColor:
												game.thumbnail.bg,
										}}
									>
										<span
											className="text-5xl opacity-70 group-hover:scale-110 group-hover:opacity-90 transition-all duration-500"
											style={{
												filter: `drop-shadow(0 0 20px ${game.thumbnail.fg}55)`,
											}}
											aria-hidden="true"
										>
											{game.thumbnail.emoji}
										</span>
									</div>
									<div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
									<div className="absolute bottom-0 left-0 right-0 p-3">
										<div className="font-display font-bold text-sm text-white">
											{
												ARCHETYPE_LABELS[
													archetype as Archetype
												]
											}
										</div>
									</div>
								</Link>
							);
						},
					)}
				</div>
			</section>

			{/* All Games */}
			<section className="space-y-5">
				<h2 className="font-display font-bold text-xl md:text-2xl text-ink">
					All games
				</h2>
				<div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
					{placeholderGames.map((g) => (
						<GameCard key={g.id} game={g} />
					))}
				</div>
			</section>
		</div>
	);
}
